import {useAtomValue} from 'jotai';
import {useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {typographyStyle} from '@/components/Typography';
import {useGA4WithParams} from '@/hooks/tracking';
import {AnalysisItem} from '@/map/@types';
import {hallSalesCategoriesAtom} from '@/map/atom';
import {Divider, interceptProps} from '@/styled-components';
import {palette} from '@/styles';
import {PopulationCompare} from './Population';
import {SalesCompare} from './Sales';

interface IProps {
  list: AnalysisItem[];
}

export const CompareReport = ({list}: IProps) => {
  const selectedCategory = useAtomValue(hallSalesCategoriesAtom);
  const [activeTab, setActiveTab] = useState<number>(0);

  const categoryTab = useMemo(
    () =>
      selectedCategory.map((category) => `매출(${category.categoryDetail})`),
    [selectedCategory]
  );

  const compareTabs = useMemo(() => {
    return ['인구', ...categoryTab];
  }, [categoryTab]);

  const tabsRef = useRef<HTMLDivElement[]>([]);

  const tracking = useGA4WithParams();

  const handleTabClick = (idx: number) => {
    setActiveTab(idx);

    tracking('analysis_compare_tabchange', {
      tab_info: compareTabs[idx],
    });

    const tabElement = tabsRef.current[idx];

    if (tabElement) {
      tabElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  };

  return (
    <>
      <Tabs>
        {compareTabs.map((i, idx) => (
          <Tab
            active={activeTab === idx}
            key={i}
            ref={(el) => {
              if (el) {
                tabsRef.current[idx] = el;
              }
            }}
            onClick={() => handleTabClick(idx)}
          >
            {i}
          </Tab>
        ))}
      </Tabs>
      <Divider />
      <ScrollBody>
        {activeTab === 0 ? (
          <PopulationCompare list={list} />
        ) : (
          categoryTab.map(
            (_, idx) =>
              activeTab === idx + 1 && (
                <SalesCompare
                  key={idx}
                  list={list}
                  category={selectedCategory[idx]}
                />
              )
          )
        )}
      </ScrollBody>
    </>
  );
};

const Tabs = styled.div`
  display: flex;
  overflow: auto;
  flex-shrink: 0;
  overflow-x: auto;
  scroll-behavior: smooth;
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently supported by Chrome, Edge, Opera and Firefox */
`;

const Tab = styled('div').withConfig({
  shouldForwardProp: interceptProps(['active']),
})<{
  active?: boolean;
}>`
  border-bottom: solid 2px transparent;
  color: ${palette.gray400};
  padding: 12px 16px;
  white-space: nowrap;
  font-weight: 500;
  ${({active}) =>
    active &&
    `
    border-color: ${palette.textPrimary};
    color: ${palette.textPrimary};
    font-weight: 700;
  `}
  ${{...typographyStyle.medium14}}
  cursor: pointer;
`;

const ScrollBody = styled.div`
  flex-grow: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${palette.gray100};
`;
